import { observer } from "mobx-react-lite";
import { CalendarDays } from "lucide-react";
import { SelectField } from "@/components/common";
import { useTran } from "@/hooks";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import {
  dashboardEChartsStore,
  E_CHARTS_MAX_YEAR_RANGE,
} from "../../../stores/e-charts.store";
import { sortReportingYearsAscending } from "../../../utils/reporting-years";

const toYearValue = (value) => {
  const n = value == null || value === "" ? null : Number(value);
  return Number.isFinite(n) ? n : null;
};

const findLastYearAtOrBefore = (years, maxYear) => {
  for (let idx = years.length - 1; idx >= 0; idx -= 1) {
    if (years[idx] <= maxYear) return years[idx];
  }
  return null;
};

const findFirstYearAtOrAfter = (years, minYear) => {
  for (const year of years) {
    if (year >= minYear) return year;
  }
  return null;
};

const buildReportingYearOptions = (years, reportingYears) => {
  const reportingYearByStartYear = new Map(
    sortReportingYearsAscending(reportingYears).map((reportingYear) => [
      Number(reportingYear.startYear),
      reportingYear,
    ]),
  );

  return years.map((year) => {
    const reportingYear = reportingYearByStartYear.get(Number(year));
    return {
      value: String(year),
      label: reportingYear
        ? formatReportingYearPeriod(reportingYear)
        : String(year),
    };
  });
};

const ReportingYearSelection = observer(
  ({ availableYears = [], reportingYears = [] }) => {
    const { t } = useTran();
    const store = dashboardEChartsStore;
    const fullYearSpan = [...availableYears].sort((a, b) => a - b);
    const startYear = store.yearRangeStart;
    const endYear = store.yearRangeEnd;
    const minStartYear = Number.isFinite(endYear)
      ? endYear - (E_CHARTS_MAX_YEAR_RANGE - 1)
      : null;
    const maxEndYear = Number.isFinite(startYear)
      ? startYear + (E_CHARTS_MAX_YEAR_RANGE - 1)
      : null;

    const startYearOptions = buildReportingYearOptions(
      fullYearSpan.filter(
        (year) =>
          (!Number.isFinite(endYear) || year <= endYear) &&
          (!Number.isFinite(minStartYear) || year >= minStartYear),
      ),
      reportingYears,
    );

    const endYearOptions = buildReportingYearOptions(
      fullYearSpan.filter(
        (year) =>
          (!Number.isFinite(startYear) || year >= startYear) &&
          (!Number.isFinite(maxEndYear) || year <= maxEndYear),
      ),
      reportingYears,
    );

    const yearSpanLabel =
      fullYearSpan.length > 0
        ? t(
            "Select a start and end point. Dynamic bounds will automatically protect the maximum {{n}}-year visualization constraints.",
            {
              n: E_CHARTS_MAX_YEAR_RANGE,
            },
          )
        : t("No reporting years available");

    const handleStartYearChange = (value) => {
      const nextStart = toYearValue(value);
      if (!Number.isFinite(nextStart)) {
        store.setYearRange({ start: null, end: null });
        return;
      }

      let nextEnd = Number.isFinite(endYear) ? endYear : nextStart;
      if (nextEnd < nextStart) nextEnd = nextStart;
      if (nextEnd > nextStart + (E_CHARTS_MAX_YEAR_RANGE - 1)) {
        nextEnd =
          findLastYearAtOrBefore(
            fullYearSpan,
            nextStart + (E_CHARTS_MAX_YEAR_RANGE - 1),
          ) ?? nextStart;
      }

      store.setYearRange({ start: nextStart, end: nextEnd });
    };

    const handleEndYearChange = (value) => {
      const nextEnd = toYearValue(value);
      if (!Number.isFinite(nextEnd)) {
        store.setYearRange({ start: null, end: null });
        return;
      }

      let nextStart = Number.isFinite(startYear) ? startYear : nextEnd;
      if (nextStart > nextEnd) nextStart = nextEnd;
      if (nextStart < nextEnd - (E_CHARTS_MAX_YEAR_RANGE - 1)) {
        nextStart =
          findFirstYearAtOrAfter(
            fullYearSpan,
            nextEnd - (E_CHARTS_MAX_YEAR_RANGE - 1),
          ) ?? nextEnd;
      }

      store.setYearRange({ start: nextStart, end: nextEnd });
    };

    const yearRangeSummary =
      Number.isFinite(startYear) && Number.isFinite(endYear)
        ? t("Showing {{start}}-{{end}}", {
            start: startYear,
            end: endYear,
          })
        : t("Select a reporting period");

    return (
      <div className="rounded-lg border border-border/70 bg-background/95 shadow-sm">
        <div className="border-b px-3 py-3">
          <div className="flex items-start gap-2">
            <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
              <CalendarDays className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {t("Reporting Year Selection")}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {yearSpanLabel}
              </p>
            </div>
          </div>
        </div>
        <div className="grid gap-3 px-3 py-3 md:grid-cols-2">
          <SelectField
            label={t("Start Year Range")}
            labelClassName="text-[11px] font-semibold text-foreground"
            options={startYearOptions}
            value={startYear == null ? "" : String(startYear)}
            onValueChange={handleStartYearChange}
            placeholder={t("Select start year")}
            emptyLabel={t("No years available")}
            sortOptions={false}
            disabled={startYearOptions.length === 0}
            selectClassName="h-10 text-sm"
          />
          <SelectField
            label={t("End Year Range")}
            labelClassName="text-[11px] font-semibold text-foreground"
            options={endYearOptions}
            value={endYear == null ? "" : String(endYear)}
            onValueChange={handleEndYearChange}
            placeholder={t("Select end year")}
            emptyLabel={t("No years available")}
            sortOptions={false}
            disabled={endYearOptions.length === 0}
            selectClassName="h-10 text-sm"
          />
        </div>
        <div className="border-t px-3 py-2">
          <p className="text-[11px] text-muted-foreground">
            {yearRangeSummary}
          </p>
        </div>
      </div>
    );
  },
);

export default ReportingYearSelection;
